import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { BehaviorSubject, filter, takeUntil } from 'rxjs';
import { Logger } from 'src/app/shared/tcs-logging/logger';
import { TcsServerResponse, toTcsServerResponse } from 'src/util/helpers/http-response-helper';
import { createSafeProps } from '../tcs-app-insights/app-insights-util';

export enum FileUploadStatus {
  Begin = 'Begin',
  Progress = 'Progress',
  Complete = 'Complete',
  Cancelled = 'Cancelled',
  Error = 'Error',
}

export interface FileUploadEvent {
  file: File;
  resource?: string;
  status: FileUploadStatus;
  progress: number;
  response?: TcsServerResponse;
}

function createFileUploadEvent(
  file: File,
  resource: string | undefined,
  previous: FileUploadEvent | null,
  current: Partial<FileUploadEvent> | null,
) {
  const event: FileUploadEvent = {
    file,
    resource,
    status: current?.status ?? previous?.status ?? FileUploadStatus.Begin,
    progress: current?.progress ?? previous?.progress ?? 0,
    response: current?.response,
  };
  return event;
}

export interface IFileUploadRequest {
  file: File;
  resource?: string;
  headers?: { [key: string]: string };
}

export abstract class FileUploader {
  private _defaultHeaders: { [key: string]: string };
  private _fileUploadEvent$ = new BehaviorSubject<FileUploadEvent | null>(null);

  public get events$() {
    return this._fileUploadEvent$.asObservable();
  }

  constructor(private _apiUrl: string, private _http: HttpClient, private _logger: Logger) {
    this._defaultHeaders = {};
  }

  public abstract validate(request: IFileUploadRequest, allowedFileTypes?: string[]): Error | null;

  public setDefaultHeaders(headers: { [key: string]: string }) {
    this._defaultHeaders = headers;
  }

  public cancelUpload() {
    const previousEvent = this._fileUploadEvent$.getValue();
    if (previousEvent != null) {
      this._fileUploadEvent$.next(
        createFileUploadEvent(previousEvent.file, previousEvent.resource, previousEvent, {
          status: FileUploadStatus.Cancelled,
        }),
      );
    }
  }

  public uploadFile(request: IFileUploadRequest, allowedFileTypes?: string[]) {
    const error = this.validate(request, allowedFileTypes);
    if (error != null) {
      this._fileUploadEvent$.next(
        createFileUploadEvent(request.file, request.resource, null, {
          status: FileUploadStatus.Error,
          response: {
            status: 'Failed',
            errors: [{ message: error.message, code: 4000 }],
          },
        }),
      );
      return;
    }

    const formData = new FormData();
    formData.append('file', request.file, request.file.name);

    const url = new URL(request.resource ?? '', this._apiUrl);
    const headers = { ...this._defaultHeaders, ...request.headers };

    this._http
      .post(url.href, formData, {
        reportProgress: true,
        observe: 'events',
        headers,
      })
      .pipe(takeUntil(this._fileUploadEvent$.pipe(filter((ev) => ev?.status === 'Cancelled'))))
      .subscribe({
        next: (event: HttpEvent<any>) => {
          const previousEvent = this._fileUploadEvent$.getValue();
          if (event.type === HttpEventType.Sent) {
            this._fileUploadEvent$.next(
              createFileUploadEvent(request.file, request.resource, null, null),
            );
          } else if (event.type === HttpEventType.UploadProgress) {
            const total = event.total ?? request.file.size;
            const progress = Math.round((100 * event.loaded) / total);
            this._fileUploadEvent$.next(
              createFileUploadEvent(request.file, request.resource, previousEvent, {
                status: FileUploadStatus.Progress,
                progress,
              }),
            );
          } else if (event.type === HttpEventType.Response) {
            const response = toTcsServerResponse(event);
            this._fileUploadEvent$.next(
              createFileUploadEvent(request.file, request.resource, previousEvent, {
                status:
                  response.status === 'Failed' ? FileUploadStatus.Error : FileUploadStatus.Complete,
                progress: 100,
                response: {
                  data: response.data,
                  status: response.status,
                  errors: response.errors,
                },
              }),
            );
          }
        },
        error: (err: HttpErrorResponse) => {
          this._fileUploadEvent$.next(
            createFileUploadEvent(
              request.file,
              request.resource,
              this._fileUploadEvent$.getValue(),
              {
                status: FileUploadStatus.Error,
                response: {
                  status: 'Failed',
                  errors: [{ message: err.message, code: err.status }],
                },
              },
            ),
          );
          this._logger.error(
            `[FileUploader] Error: ${err.message}`,
            createSafeProps({ error: err }),
          );
        },
        complete: () => {
          this._fileUploadEvent$.next(null);
        },
      });
  }
}
